import { atom, selector } from "recoil"

import type {
  QuoteResult,
  Quote,
  PoisonedQuote,
  LoanProduct,
} from "~/graphql/client"

import {
  byFederalThenValue,
  proposalRemoteState,
} from "../proposal/proposal-state"

export const quotesRemoteState = atom<QuoteResult[]>({
  key: `quotesRemoteState`,
  default: [],
})

/**
 * Savings query scope last written by a successful getQuotesForProposal.
 * Compared to the current finance selection scope to know whether this
 * product's quotes snapshot has settled.
 */
export const quotesSnapshotSettledScopeState = atom<string | null>({
  key: `quotesSnapshotSettledScope`,
  default: null,
})

const mostRecentQuote = (quotes: QuoteResult[]) => {
  const [quote] = [...quotes].sort((a, b) => a.createdAt - b.createdAt)

  return quote ?? null
}

export const safeQuoteRemoteState = selector<Quote | null>({
  key: `safeQuoteRemoteState`,
  get: ({ get }) => {
    const quotes = get(quotesRemoteState)
    const mostRecent = mostRecentQuote(quotes)

    return mostRecent?.__typename === `Quote` ? mostRecent : null
  },
})

export const poisonedQuoteRemoteState = selector<PoisonedQuote | null>({
  key: `poisonedQuoteRemoteState`,
  get: ({ get }) => {
    const quotes = get(quotesRemoteState)
    const mostRecent = mostRecentQuote(quotes)

    return mostRecent?.__typename === `PoisonedQuote` ? mostRecent : null
  },
})

export const selectedLoanPaymentsState = selector<{
  quote: Quote | null
  selectedLoan: LoanProduct | undefined
  applyingFederal: boolean
}>({
  key: `selectedLoanPayments`,
  get: ({ get }) => {
    const quote = get(safeQuoteRemoteState)
    const proposal = get(proposalRemoteState)
    const { selectedLoanProduct } = proposal
    const selectedLoan = selectedLoanProduct ?? undefined

    const incentivesToApply =
      proposal.incentives
        ?.filter((i) => i.applyToLoan && i.showOnBrochure)
        .sort(byFederalThenValue()) ?? []

    const applyingFederal = !!incentivesToApply.some(
      (i) => i.showOnBrochure && i.applyToLoan,
    )

    return { quote, selectedLoan, applyingFederal }
  },
})
